import React from 'react';
import CombinedSection from './CombinedSection';
import { FaClipboard } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../contexts/NotificationContext';
import { generateContentSections } from '../../constants/sectionConfig';

interface Props {
  content: Record<string, string | null>;
}

const GeneratedContent: React.FC<Props> = ({ content }) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      showNotification(t('common.copied_to_clipboard'), 'success');
    });
  };

  const processContent = (content: string | null): JSX.Element | null => {
    if (!content) return null;

    // First clean any markdown code block formatting
    const cleanedContent = content
      .replace(/^```(?:json|html)?\n/, '')
      .replace(/\n```$/, '')
      .trim();

    try {
      // Try parsing as JSON first
      const parsed = JSON.parse(cleanedContent);

      // Handle case where the parsed content is already an array
      if (Array.isArray(parsed)) {
        return (
          <div className="space-y-2">
            {parsed.map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-between relative group p-3 bg-gray-50 dark:bg-gray-750 rounded-lg border border-gray-200 dark:border-gray-700"
              >
                <span className="text-gray-700 dark:text-gray-300">{String(item)}</span>
                <button
                  onClick={() => copyToClipboard(String(item))}
                  className="absolute top-2 right-2 p-2 bg-white dark:bg-gray-700 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Copy to clipboard"
                >
                  <FaClipboard className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" />
                </button>
              </div>
            ))}
          </div>
        );
      }

      // Handle case where it's an object with arrays
      if (typeof parsed === 'object' && parsed !== null) {
        return (
          <div className="space-y-2">
            {Object.values(parsed)
              .flat()
              .map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between relative group p-3 bg-gray-50 dark:bg-gray-750 rounded-lg border border-gray-200 dark:border-gray-700"
                >
                  <span className="text-gray-700 dark:text-gray-300">{String(item)}</span>
                  <button
                    onClick={() => copyToClipboard(String(item))}
                    className="absolute top-2 right-2 p-2 bg-white dark:bg-gray-700 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Copy to clipboard"
                  >
                    <FaClipboard className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" />
                  </button>
                </div>
              ))}
          </div>
        );
      }
    } catch {
      // If JSON parsing fails, render as HTML
      return (
        <div
          className="prose dark:prose-invert"
          dangerouslySetInnerHTML={{ __html: cleanedContent }}
        />
      );
    }

    // Fallback return (should not reach here)
    return null;
  };

  const sections = generateContentSections();

  return (
    <div className="space-y-6 bg-white dark:bg-gray-800 rounded-lg p-6 mt-8">
      {sections.map(
        ({ key, title, isTextSection }) =>
          content[key] && (
            <CombinedSection
              key={key}
              title={t(title)}
              content={content[key]}
              processContent={processContent}
              copyToClipboard={copyToClipboard}
              isTextSection={isTextSection}
            />
          )
      )}
    </div>
  );
};

export default GeneratedContent;
